import { useAdsContext } from "@/ads/context";
import { useFarbwelt } from "@/hooks";
import { AdSlotIds } from "@/shared/types";
import { isFeatureFlagActive } from "@/utils/kameleoon/isFeatureFlagActive";
import { useCallback, useEffect, useRef } from "react";
/**  initialize google ads and define slots
 * 1. wait for GPT to be ready
 * 2. setup GPT
 * 3. define slots
 * 4. set targeting
 * 5. enable services
 * @returns void
 *
 */
export const useGoogleAdsInit = () => {
    const { adSlots, targeting, registerSlot, setGptInitialized, setLoadedSlot, hasDeviceTypeChanged, userDevice, loadedSlots, deleteAllSlots, } = useAdsContext();
    const isInitialized = useRef(false);
    const deviceTypeChanged = useRef(undefined); // to check if device type has changed only once
    const retry = useRef(0); // retry counter for checking if GPT is ready
    const limit = useRef(350); // retry limit for checking if GPT is ready
    const { isFarbweltActive } = useFarbwelt();
    /**
     * set targeting for the ad
     * if slot is provided, set targeting on slot level
     * else set targeting on pubads level
     * @param targeting
     * @param slot
     */
    const setTargeting = (targeting, slot) => {
        Object.entries(targeting).forEach(([key, value]) => {
            // targeting can be done on slot level or on pubads level
            if (slot) {
                slot.setTargeting(key, Array.isArray(value) ? value : String(value));
            }
            else {
                googletag
                    .pubads()
                    .setTargeting(key, Array.isArray(value) ? value : String(value));
            }
        });
    };
    const defineSlots = useCallback(() => {
        // filter out leaderboard ad slot if hideadleaderboard feature flag is active
        const hideAdLeaderboard = isFeatureFlagActive("hideadleaderboard");
        /** to avoid yieldLove issue */
        /** define slots that will not be defined during first load */
        const dynamicAdSlots = [AdSlotIds.AD_MASONRY, AdSlotIds.AD_CAROUSEL]; // those slots are not loaded directly but refresh when their dom container is available
        /** define slots that will will be defined if farbwelt is active */
        const farbweltAdSlots = [
            AdSlotIds.PARTNERBOX_1_COMMERCIAL,
            AdSlotIds.PARTNERBOX_2_COMMERCIAL,
            AdSlotIds.PARTNERBOX_3_TOOLS_AND_SERVICE,
            AdSlotIds.PARTNERBOX_4_TOOLS_AND_SERVICE,
            AdSlotIds.PARTNERBOX_5_TOOLS_AND_SERVICE,
            AdSlotIds.RENTAL_LINK,
        ];
        // Filter with farbwelt ad slots if farbwelt is active
        // OR Filter out dynamic ad slots if farbwelt is not active
        // and hide leaderboard if hideAdLeaderboard feature flag is active
        // This will ensure that only the relevant ad slots are defined
        const usedAdslots = isFarbweltActive
            ? adSlots.filter((ad) => farbweltAdSlots.includes(ad.id))
            : adSlots.filter((ad) => !dynamicAdSlots.includes(ad.id) &&
                (!hideAdLeaderboard || ad.id !== AdSlotIds.AD_LEADERBOARD) // ABTEST
            );
        // Define the slots using googletag
        usedAdslots.forEach((ad) => {
            var _a;
            const slot = (_a = googletag
                .defineSlot(ad.adUnit, ad.size, ad.id)) === null || _a === void 0 ? void 0 : _a.addService(googletag.pubads());
            // Add slot-level targeting if available
            if (ad.specificTargeting) {
                setTargeting(ad.specificTargeting, slot);
            }
            // Keep track of this slot
            if (slot) {
                registerSlot(slot.getSlotElementId(), slot);
            }
        });
        const slotStatus = {};
        // Listen for ad render events to track loaded slots
        googletag.pubads().addEventListener("slotRenderEnded", (event) => {
            const slotId = event.slot.getSlotElementId();
            // Avoid overwriting if already marked as loaded
            // because yieldLove also fires slotRenderEnded event
            // and we don't want to overwrite the status and avoid conflict
            // If the slot is already loaded, exit early
            if (slotStatus[slotId] === "loaded")
                return;
            // Update the status of the slot
            // If the slot is empty, mark it as "failed"
            // Otherwise, mark it as "loaded"
            const status = event.isEmpty ? "failed" : "loaded";
            slotStatus[slotId] = status;
            setLoadedSlot(slotId, status);
        });
    }, [adSlots, registerSlot, setLoadedSlot, isFarbweltActive]);
    const displayAndRefreshSlots = useCallback(() => {
        // Display and refresh all loaded slots
        Object.keys(loadedSlots).forEach((slotId) => {
            window.googletag.display(slotId);
        });
        window.googletag.pubads().refresh();
    }, [loadedSlots]);
    // Setup and initialize Google Publisher Tags (GPT)
    const initializeGPT = useCallback(() => {
        googletag.cmd.push(() => {
            // Set global targeting parameters
            setTargeting(targeting);
            // Enable lazy loading for ads
            // This will load ads only when they are needed
            // This helps improve page load performance
            googletag.pubads().enableLazyLoad({
                fetchMarginPercent: userDevice === "desktop" ? 300 : 400, // 3 || 4 viewports ahead
                renderMarginPercent: userDevice === "desktop" ? 75 : 100, // .75 || 1.0 viewports ahead
                mobileScaling: 2.5, // 2.5 for mobile
            });
            defineSlots();
            // Configure GPT services
            // Enable single request mode for better performance
            // This allows multiple ad slots to be requested in a single call
            // instead of separate calls for each slot
            googletag.pubads().enableSingleRequest();
            // Track slot render results
            // Enable the GPT services
            googletag.enableServices();
        });
    }, [targeting, defineSlots, userDevice]);
    const hasTriggeredSlotReset = useRef(false);
    // Trigger cleanup on device change
    useEffect(() => {
        if (hasDeviceTypeChanged && deviceTypeChanged.current !== userDevice) {
            googletag.cmd.push(() => {
                googletag.destroySlots();
                deleteAllSlots(); //  empty loadedSlots
            });
            hasTriggeredSlotReset.current = true;
            deviceTypeChanged.current = userDevice;
        }
    }, [hasDeviceTypeChanged, userDevice, deleteAllSlots]);
    // Wait until loadedSlots is empty, then define & display
    useEffect(() => {
        if (hasTriggeredSlotReset.current &&
            Object.keys(loadedSlots).length === 0) {
            googletag.cmd.push(() => {
                defineSlots();
                displayAndRefreshSlots();
            });
            hasTriggeredSlotReset.current = false; // reset the trigger
        }
    }, [loadedSlots, defineSlots, displayAndRefreshSlots]);
    const waitForGPT = useCallback((onReady) => {
        const tryInitialize = () => {
            var _a, _b;
            const isGPTAvailable = ((_a = window === null || window === void 0 ? void 0 : window.googletag) === null || _a === void 0 ? void 0 : _a.apiReady) &&
                typeof (googletag === null || googletag === void 0 ? void 0 : googletag.pubads) === "function" &&
                typeof ((_b = googletag === null || googletag === void 0 ? void 0 : googletag.pubads()) === null || _b === void 0 ? void 0 : _b.setTargeting) === "function";
            if (isInitialized.current)
                return;
            if (isGPTAvailable && adSlots.length > 0) {
                isInitialized.current = true;
                setGptInitialized(true);
                onReady();
            }
            else if (retry.current < limit.current) {
                retry.current++;
                setTimeout(tryInitialize, 100);
            }
            else {
                return;
            }
        };
        tryInitialize();
    }, [setGptInitialized, isInitialized, retry, limit, adSlots]);
    useEffect(() => {
        if (isInitialized.current)
            return;
        waitForGPT(initializeGPT);
    }, [waitForGPT, initializeGPT, isInitialized]);
};
